import { Call } from '@twilio/voice-sdk';

/**
 * A quality warning raised by the Voice SDK during a call.
 *
 * `constant-audio-input-level` is the one that matters most here: the SDK raises it after ten
 * consecutive identical input-volume samples, which is what a microphone that captures nothing looks
 * like from inside the call. It is the only in-call signal that the care manager is talking into a
 * dead track, and the SDK suppresses it while the call is muted, so it never fires spuriously.
 */
export interface CallAudioWarning {
  /** SDK warning name, e.g. 'constant-audio-input-level', 'high-packet-loss'. */
  name: string;
  /** True when the SDK cleared a previously raised warning. */
  cleared: boolean;
  callSid?: string;
  call?: Call;
  /** The SDK's own payload, absent for warnings the Call raises itself. See {@link callWarningDetector}. */
  data?: CallWarningData | null;
}

/** The shape the Voice SDK hands to a `warning` listener when the StatsMonitor raised it. */
export interface CallWarningData {
  /** The measured statistic — for `minStandardDeviation`, the standard deviation itself. */
  value?: number;
  values?: number[];
  threshold?: { name?: string; value?: number };
}

/**
 * Which of the two detectors behind `constant-audio-input-level` fired.
 *
 * The SDK raises that one warning name from two different places, and they mean different things:
 *
 * - `flatline` — `Call._checkVolume`, which needs ten consecutive input-volume readings that are
 *   EXACTLY equal. With floating point values that is effectively only true of digital silence.
 *   The SDK passes no payload for it, which is what identifies it here.
 * - `stddev` — the StatsMonitor's `minStandardDeviation` threshold (327.67 on a 0–32767 scale, about
 *   -40 dBFS), which measures flatness rather than silence: a steady enough room tone can raise it
 *   on a working microphone.
 *
 * Recording the difference costs nothing and keeps a false positive from being read as proof.
 */
export const callWarningDetector = (data?: CallWarningData | null): 'flatline' | 'stddev' =>
  data?.threshold?.name === 'minStandardDeviation' ? 'stddev' : 'flatline';

/**
 * Whether a warning should raise the care manager's alarm banner.
 *
 * Only `flatline` does. Both detectors carry the same warning name, but they do not carry the same
 * claim: `flatline` means nothing was captured, while `stddev` means the level was very steady,
 * which a constant room tone can produce on a microphone that works. Raising one persistent "the
 * patient cannot hear you" banner for both would put a claim we cannot support in front of a nurse
 * mid-call, and an alarm that cries wolf is one nurses learn to ignore — including when it is real.
 *
 * `stddev` is still reported to the backend, with its measured standard deviation. When that data
 * shows it predicts a dead channel, it earns a banner — and its own wording, not this one.
 */
export const raisesMicrophoneAlarm = (warning: CallAudioWarning): boolean =>
  warning.name === MICROPHONE_DEAD_WARNING && !warning.cleared && callWarningDetector(warning.data) === 'flatline';

/**
 * Whether a warning clears the alarm.
 *
 * Deliberately not the mirror of {@link raisesMicrophoneAlarm}: the SDK sends no payload with a
 * cleared warning, so insisting on a detector here would leave a raised banner on screen forever.
 * Clearing something that was never raised is harmless; failing to clear is not.
 */
export const clearsMicrophoneAlarm = (warning: CallAudioWarning): boolean =>
  warning.name === MICROPHONE_DEAD_WARNING && warning.cleared;

/**
 * Warnings worth reporting to the backend; the rest are noise for this purpose.
 *
 * Names exactly as the SDK builds them (`call.js`: prefix from the threshold + stat name). This list
 * used to carry `high-rtp-packet-loss`, which the SDK never emits, so packet loss — the commonest
 * cause of choppy audio — was never reported at all. `high-packet-loss` is a single bad sample
 * (`max`), `high-packets-lost-fraction` a sustained average (`maxAverage`).
 */
export const REPORTED_CALL_WARNINGS: readonly string[] = [
  'constant-audio-input-level',
  'high-packet-loss',
  'high-packets-lost-fraction',
  'high-jitter',
  'high-rtt',
  'low-mos',
  'ice-connectivity-lost',
];

/** The single warning that means "the patient cannot hear this care manager". */
export const MICROPHONE_DEAD_WARNING = 'constant-audio-input-level';
